// @ts-nocheck
import React, { useContext, useEffect, useRef, useState } from 'react';
import FixedButton from '@core-ui/fixed-button';
import { DrawerSlidePosition } from '@core-ui/motion';
import { useLocalize } from '@core-ui/localization-provider';
// import { useSitewide } from '@mfe/sitewide';
import { Drawer } from '@core-ui/drawer';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { useAppState } from '@core-ui/app-state-provider';
import {
  css,
  CSSObject,
  forBrands,
  getFontWeight,
  Theme,
} from '@core-ui/react-stitch';
import {
  isDashboard,
  isDrawer,
  isTop,
  Position,
} from '../../collaborators/positions';
import scrollOnClose from '../../collaborators/scroll-on-close';
import FilterIcon from './components/FilterIcon';
import CloseButton from './styled/CloseButton';
import Dashboard from './styled/Dashboard';
import DashboardItem from './styled/DashboardItem';
import DashboardTitle from './styled/DashboardTitle';
import DrawerContent from './styled/DrawerContent';
import Divider from './styled/Divider';
import {
  DASHBOARD_FILTER,
  MOBILE_FACETS_WRAPPER_DONE_TEXT,
  FILTERS_ARIA_LABEL,
  CLOSE_ARIA_LABEL,
} from './translations';

interface MobileProps {
  children: ComponentProps[];
  newDashboard: boolean;
  isModelToggleEnabled: boolean;
  hasMarketingDataEBB?: boolean;
  appliedFacetsCount: number;
}

interface ComponentProps extends Node {
  props: {
    htmlFor?: string;
    title?: string;
    position: Position;
  };
}

// eslint-disable-next-line complexity
const Mobile = (props: MobileProps) => {
  const {
    children,
    appliedFacetsCount,
    newDashboard = false,
    isModelToggleEnabled = false,
    hasMarketingDataEBB = false,
  } = props;

  const { brandName } = useAppState();
  const isBR = brandName === 'br' || brandName === 'brfs';

  // const { totalStickyElementHeight } = useSitewide();
  const { greaterOrEqualTo } = useContext(BreakpointContext);
  // If rendering BR redesign, this Mobile component is rendered for desktop
  const isDesktop = greaterOrEqualTo(XLARGE);

  const brMobileStyles = (): CSSObject =>
    ({
      position: 'sticky',
      top: isDesktop ? 118 : 79,
      zIndex: 100,
      padding: '0',
    } as CSSObject);

  const brTriggerButtonStyles = (theme: Theme): CSSObject =>
    ({
      '&:hover': {
        backgroundColor: theme.color.b2,
      },
      backgroundColor: `${theme.color.b2}00`,
      color: theme.color.inverse.b1,
      textTransform: 'capitalize',
      fontWeight: getFontWeight('book').fontWeight,
    } as CSSObject);

  const brDrawerStyles = (theme: Theme): CSSObject =>
    ({
      backgroundColor: theme.color.inverse.b1,
      maxWidth: '20.3129rem',
    } as CSSObject);

  const brFixedButtonStyles = (theme: Theme): CSSObject =>
    ({
      backgroundColor: theme.color.b2,
      color: theme.color.inverse.b1,
    } as CSSObject);

  const mobileWrapper = () =>
    hasMarketingDataEBB && isBR ? { marginTop: '-45px', zIndex: '100' } : {};

  const mobileStyles = (theme: Theme) =>
    css({
      width: '100%',
      padding: '0 1rem',
      ...(forBrands(theme, {
        br: brMobileStyles,
        brfs: brMobileStyles,
      }) as CSSObject),
    });

  const triggerButtonCSS = (theme: Theme) =>
    css({
      background: 'none',
      border: 'none',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      outline: 'none',
      color: theme.color.b1,
      textTransform: 'uppercase',
      paddingLeft: '0',
      margin: '0.25rem 0',
      '& svg': {
        marginRight: '0.2rem',
      },
      ...(newDashboard
        ? {}
        : {
            ...theme.brandFont,
            fontSize: '0.9rem',
            fontWeight: isBR ? getFontWeight('light').fontWeight : 'bold',
            color: theme.color.b1,
            background: 'none',
            border: 'none',
            margin: '0',
            textTransform: 'uppercase',
            padding: 'unset',
          }),
      ...(forBrands(theme, {
        br: () => brTriggerButtonStyles(theme),
        brfs: () => brTriggerButtonStyles(theme),
      }) as CSSObject),
    });

  const drawerBottomRef = useRef<HTMLDivElement>(null);

  const [shouldScroll, setShouldScroll] = useState(false);
  const [drawerBottomOffsetHeight, setDrawerBottomOffsetHeight] = useState(0);
  const [open, setOpen] = useState(false);

  const openDrawer = (): void => {
    setOpen(true);
    setShouldScroll(!isBR);

    setTimeout(() => {
      setDrawerBottomOffsetHeight(drawerBottomRef?.current?.offsetHeight ?? 0);
    }, 1000);
  };

  const closeDrawer = (): void => {
    setOpen(false);
    setShouldScroll(!isBR);
  };

  const { localize } = useLocalize();
  const components = React.Children.map(children, (child) => child) ?? [];
  const topComponents = components.filter(isTop);
  const dashboardComponents = components.filter(isDashboard);
  const drawerComponents = components.filter(isDrawer);
  const showFacetCountLabel =
    Number.isInteger(appliedFacetsCount) && appliedFacetsCount >= 1
      ? `(${appliedFacetsCount})`
      : '';
  const filterText = localize(DASHBOARD_FILTER);
  const triggerButtonPrefix = newDashboard ? (
    <FilterIcon />
  ) : (
    filterText?.toLowerCase()
  );
  const triggerButtonText = (
    <>
      {triggerButtonPrefix} {showFacetCountLabel}
    </>
  );

  const drawerPosition = isBR
    ? DrawerSlidePosition.left
    : DrawerSlidePosition.right;

  useEffect(() => {
    if (shouldScroll) {
      scrollOnClose(setShouldScroll);
    }
  }, [open, shouldScroll]);

  return (
    <div css={mobileStyles} style={mobileWrapper()}>
      {topComponents}
      <Dashboard newDashboard={newDashboard}>
        <DashboardItem
          isModelToggleEnabled={isModelToggleEnabled}
          newDashboard={newDashboard}
        >
          <div className="filter-container">
            {newDashboard && (
              <DashboardTitle htmlFor="filterButton" withChevron={false}>
                {filterText}
              </DashboardTitle>
            )}
            <button
              className="filter_button"
              css={triggerButtonCSS}
              id="filterButton"
              onClick={openDrawer}
              type="button"
            >
              {triggerButtonText}
            </button>
            <Drawer
              aria-label={localize(FILTERS_ARIA_LABEL)}
              container=".cat-drawer"
              dialogCSS={(theme) => ({
                width: '80%',
                maxWidth: '20rem',
                ...(forBrands(theme, {
                  br: () => brDrawerStyles(theme),
                  brfs: () => brDrawerStyles(theme),
                }) as CSSObject),
              })}
              id="facets"
              isOpen={open}
              onClose={closeDrawer}
              position={drawerPosition}
            >
              <DrawerContent>
                <CloseButton
                  ariaLabel={localize(CLOSE_ARIA_LABEL)}
                  buttonSide={isBR ? 'right' : 'left'}
                  onClick={closeDrawer}
                />
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: `calc(100% - ${drawerBottomOffsetHeight}px)`,
                  }}
                >
                  {drawerComponents}
                </div>
                <div
                  ref={drawerBottomRef}
                  css={(theme) => ({
                    padding: '1rem',
                    borderTop: `1px solid ${theme.color.gray20}`,
                  })}
                >
                  <FixedButton
                    css={(theme) => ({
                      ...(forBrands(theme, {
                        br: () => brFixedButtonStyles(theme),
                        brfs: () => brFixedButtonStyles(theme),
                      }) as CSSObject),
                    })}
                    kind="primary"
                    onClick={closeDrawer}
                  >
                    {localize(MOBILE_FACETS_WRAPPER_DONE_TEXT)}
                  </FixedButton>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </DashboardItem>
        {dashboardComponents.map((component, key) => (
          <React.Fragment key={key}>
            {newDashboard && isModelToggleEnabled ? <Divider /> : null}
            <DashboardItem
              isModelToggleEnabled={isModelToggleEnabled}
              newDashboard={newDashboard}
            >
              {newDashboard && (
                <DashboardTitle
                  htmlFor={component.props?.children?.props?.htmlFor}
                >
                  {component.props.title}
                </DashboardTitle>
              )}
              {component}
            </DashboardItem>
          </React.Fragment>
        ))}
      </Dashboard>
    </div>
  );
};

export default Mobile;
